import React from 'react';

import { isAxiosError } from 'axios';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import { Tooltip } from '@mui/material';

import { Button } from 'components/Button';
import { RequiredAsterisk } from 'components/EditEntityRow/styles';
import UploadAttachment from 'components/Entities/EntityAttachmentTab/components/UploadAttachment';
import EvidenceFiles from 'components/EvidenceFiles/EvidenceFiles';
import { StyledTextField } from 'components/Input/styles';
import NavigationBlocker from 'components/NavigationBlocker';
import {
  ContentContainer,
  FieldLabel,
  FooterContainer,
} from 'containers/Tasks/components/ActionsSchedulesTable/ScheduleDrawer/styles';
import API from 'utils/api';
import { isBadRequest, isConflict } from 'utils/api/errors';
import { conflictDetail } from 'utils/library';
import { RiskActionDetail } from 'utils/api/modules/risks';
import { useStore } from 'utils/hooks/store';

import ControlActionFields from './ControlActionFields';
import { pickedEvidenceFiles } from './evidenceFiles';
import { EvidenceSchema, EvidenceValues, withFiles } from './evidenceSchema';
import { StyledEvidenceFormLayout, StyledEvidenceHint, StyledFieldStack, StyledSubmitWrapper } from './styles';

interface EvidenceFormProps {
  action: RiskActionDetail;
  onSubmitted: (action: RiskActionDetail) => void;
  onConflict: () => void;
}

const EvidenceFields: React.FunctionComponent<{ action: RiskActionDetail }> = ({ action }) => {
  const { values, errors, touched, dirty, isSubmitting, handleChange, handleBlur, setFieldValue } =
    useFormikContext<EvidenceValues>();
  const hasText = Boolean(values.text.trim());

  return (
    <>
      <NavigationBlocker
        when={dirty && !isSubmitting}
        shouldBlockNavigation={() => dirty}
        navigateMessage="Your evidence hasn't been submitted. Leave anyway?"
      />
      <ContentContainer>
        <ControlActionFields action={action} />
        <StyledFieldStack data-testid="control-action-evidence">
          <FieldLabel id="control-action-evidence-label">
            Evidence<RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
          </FieldLabel>
          <StyledEvidenceHint>Describe what was done to put the control in place.</StyledEvidenceHint>
          <StyledTextField
            name="text"
            value={values.text}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            minRows={3}
            fullWidth
            error={Boolean(touched.text && errors.text)}
            helperText={touched.text && errors.text}
            inputProps={{ 'aria-required': true, 'aria-labelledby': 'control-action-evidence-label' }}
          />
        </StyledFieldStack>
        <StyledFieldStack data-testid="control-action-attachments">
          <FieldLabel>Attachments</FieldLabel>
          <UploadAttachment
            multiple
            uploadFiles={async (picked) => {
              await setFieldValue('files', withFiles(values.files, picked), true);
            }}
          />
          <EvidenceFiles
            files={pickedEvidenceFiles(values.files)}
            onRemove={(name) =>
              setFieldValue(
                'files',
                values.files.filter((file) => file.name !== name),
                true
              )
            }
          />
          {typeof errors.files === 'string' && <StyledEvidenceHint role="alert">{errors.files}</StyledEvidenceHint>}
        </StyledFieldStack>
      </ContentContainer>
      <FooterContainer>
        <Tooltip title={hasText ? '' : 'Enter the evidence first.'} arrow>
          <StyledSubmitWrapper tabIndex={hasText ? undefined : 0}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!hasText || isSubmitting}
              data-testid="control-action-submit"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </StyledSubmitWrapper>
        </Tooltip>
      </FooterContainer>
    </>
  );
};

const EvidenceForm: React.FunctionComponent<EvidenceFormProps> = ({ action, onSubmitted, onConflict }) => {
  const { notification } = useStore();
  const inFlight = React.useRef(false);

  const submit = async (values: EvidenceValues, helpers: FormikHelpers<EvidenceValues>): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { data } = await API.risks.submitControlEvidence(action.uuid, values.text.trim(), values.files)();
      helpers.resetForm();
      onSubmitted(data);
      notification.enqueueSuccessSnackbar(`Submitted. ${data.control.name} is now in place.`);
    } catch (error) {
      if (isAxiosError(error) && isBadRequest(error)) {
        const body = (error.response?.data ?? {}) as Partial<Record<'text' | 'files', string[]>>;
        if (body.text) helpers.setFieldError('text', body.text[0]);
        if (body.files) helpers.setFieldError('files', body.files[0]);
      } else if (isAxiosError(error) && isConflict(error)) {
        notification.enqueueErrorSnackbar(conflictDetail(error) ?? "Couldn't submit. Try again.");
        onConflict();
      } else {
        notification.enqueueErrorSnackbar("Couldn't submit. Try again.");
      }
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <Formik<EvidenceValues> initialValues={{ text: '', files: [] }} validationSchema={EvidenceSchema} onSubmit={submit}>
      <StyledEvidenceFormLayout>
        <EvidenceFields action={action} />
      </StyledEvidenceFormLayout>
    </Formik>
  );
};

export default EvidenceForm;
